import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useUnifiedNotifications } from './useUnifiedNotifications';

export const useSocketNotification = () => {
  const socketRef = useRef<Socket | null>(null);

  // Use the unified notification system
  const { isFirebaseAvailable } = useUnifiedNotifications(socketRef);

  useEffect(() => {
    // Only initialize socket if Firebase is not available
    if (isFirebaseAvailable) {
      console.log('Using Firebase for notifications, socket disabled');
      return;
    }

    // Get the current user's email from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const email = user.username;

    if (!email) {
      console.error('No user email found');
      return;
    }

    // Initialize socket connection
    const socketUrl = import.meta.env.VITE_SOCKET_BASE_URL;
    // console.log('Attempting to connect to socket server:', socketUrl);
    
    try {
      socketRef.current = io(socketUrl, {
        auth: { email },
        transports: ['websocket', 'polling'], // Try both WebSocket and polling
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
        timeout: 10000, // 10 second timeout
        forceNew: true,
        path: '/socket.io/',
        secure: true,
        rejectUnauthorized: false // Try this if SSL verification is failing
      });
    } catch (error) {
      console.error('Failed to initialize socket connection:', error);
      return;
    }

    // Connection event handlers
    socketRef.current.on('connect', () => {
      console.log('Connected to notification server (Socket.io fallback)');
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      console.log('Connection details:', {
        url: socketUrl,
        transport: socketRef.current?.io?.engine?.transport?.name,
        protocol: window.location.protocol,
        readyState: socketRef.current?.io?.engine?.readyState
      });
    });

    socketRef.current.on('error', (error) => {
      console.error('Socket error:', error);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    // Handle transport errors
    socketRef.current.io.engine.on('upgrade', () => {
      console.log('Socket transport upgraded to:', socketRef.current?.io?.engine?.transport?.name);
    });

    socketRef.current.io.engine.on('upgradeError', (error) => {
      console.error('Socket upgrade error:', error);
    });

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isFirebaseAvailable]);

  return socketRef.current;
};